import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { formatCurrency } from "@/lib/utils";
import { Application, License } from "@shared/schema";
import { subMonths } from "date-fns";

const CHART_COLORS = [
  "#3b82f6", // Blue
  "#10b981", // Emerald  
  "#f59e0b", // Amber
  "#ef4444", // Red
  "#8b5cf6", // Violet
  "#06b6d4", // Cyan
  "#84cc16", // Lime
  "#f97316", // Orange
  "#ec4899", // Pink
  "#14b8a6", // Teal
  "#6366f1", // Indigo
  "#a855f7", // Purple
  "#22c55e", // Green
  "#eab308", // Yellow
  "#64748b", // Slate
  "#94a3b8", // Secondary slate
];

type GroupByOption = "application" | "category" | "billingCycle";

interface CostBreakdownChartProps {
  startDate?: Date;
  endDate?: Date;
}

export default function CostBreakdownChart({
  startDate = subMonths(new Date(), 3),
  endDate = new Date(),
}: CostBreakdownChartProps) {
  const [groupBy, setGroupBy] = useState<GroupByOption>("application");

  const { data: applications = [], isLoading: appsLoading } = useQuery<Application[]>({
    queryKey: ['/api/applications'],
  });

  const { data: allLicenses = [], isLoading: licensesLoading } = useQuery<License[]>({
    queryKey: ['/api/licenses'],
  });

  // Filter to licenses active within the selected date range
  const licenses = allLicenses.filter((license) => {
    const licStart = new Date(license.startDate);
    const licRenewal = license.renewalDate ? new Date(license.renewalDate) : null;
    return licStart <= endDate && (!licRenewal || licRenewal >= startDate);
  });

  const isLoading = appsLoading || licensesLoading;

  // Transform data for the chart
  const getChartData = () => {
    if (!applications.length && !licenses.length) return [];

    if (groupBy === "application") {
      const appCosts: Record<number, number> = {};
      licenses.forEach((license) => {
        appCosts[license.applicationId] = (appCosts[license.applicationId] || 0) + license.cost;
      });
      return Object.entries(appCosts).map(([appId, cost]) => {
        const app = applications.find((a) => a.id === parseInt(appId));
        return { name: app?.name || 'Unknown', value: cost, id: appId };
      }).sort((a, b) => b.value - a.value);

    } else if (groupBy === "category") {
      const categoryCosts: Record<string, number> = {};
      licenses.forEach((license) => {
        const app = applications.find((a) => a.id === license.applicationId);
        const category = (app as any)?.category || 'Other';
        categoryCosts[category] = (categoryCosts[category] || 0) + license.cost;
      });
      return Object.entries(categoryCosts).map(([category, cost]) => ({
        name: category, value: cost,
      })).sort((a, b) => b.value - a.value);

    } else {
      const cycleCosts: Record<string, number> = {};
      licenses.forEach((license) => {
        const cycle = license.billingCycle;
        cycleCosts[cycle] = (cycleCosts[cycle] || 0) + license.cost;
      });
      return Object.entries(cycleCosts).map(([cycle, cost]) => ({
        name: cycle.charAt(0).toUpperCase() + cycle.slice(1), value: cost,
      })).sort((a, b) => b.value - a.value);
    }
  };

  // Utility functions
  const getTotalCost = () => licenses.reduce((sum, license) => sum + license.cost, 0);

  const truncateText = (text: string, maxLength: number = 20) => {
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const customTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const percentage = Math.round((payload[0].value / getTotalCost()) * 100);
      return (
        <div className="bg-white p-4 border border-slate-200 shadow-lg rounded-lg">
          <p className="font-semibold text-slate-900 mb-2">{payload[0].name}</p>
          <div className="space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Amount:</span>
              <span className="text-sm font-medium text-primary">
                {formatCurrency(payload[0].value)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Percentage:</span>
              <span className="text-sm font-medium text-slate-900">
                {percentage}% of total
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom Legend Component
  const CustomLegend = ({ chartData }: { chartData: any[] }) => {
    if (chartData.length === 0) return null;

    return (
      <div className="mt-6">
        <h4 className="text-sm font-medium text-slate-700 mb-3">Cost Distribution</h4>
        <ScrollArea className="h-48 w-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2">
            {chartData.map((entry, index) => {
              const percentage = Math.round((entry.value / getTotalCost()) * 100);
              return (
                <div 
                  key={`legend-${index}`} 
                  className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-md group transition-colors"
                >
                  <div className="flex items-center min-w-0 flex-1">
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0 mr-2"
                      style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                    />
                    <span 
                      className="text-sm text-slate-700 truncate group-hover:text-slate-900 transition-colors"
                      title={entry.name}
                    >
                      {truncateText(entry.name, 25)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 ml-2 flex-shrink-0">
                    <span className="text-xs font-medium text-slate-600">
                      {percentage}%
                    </span>
                    <span className="text-xs font-medium text-slate-900">
                      {formatCurrency(entry.value)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </div>
    );
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="text-xl font-bold text-slate-900">Cost Breakdown</CardTitle>
            <p className="text-sm text-slate-600 mt-1">Analyze your spending distribution</p>
          </div>
          <Select defaultValue={groupBy} onValueChange={(value) => setGroupBy(value as GroupByOption)}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Group by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="application">By Application</SelectItem>
              <SelectItem value="category">By Category</SelectItem>
              <SelectItem value="billingCycle">By Billing Cycle</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-slate-900">{formatCurrency(getTotalCost())}</h3>
            <p className="text-sm font-medium text-slate-600">Total Monthly Cost</p>
          </div>
          <div className="text-center">
            <h3 className="text-2xl font-bold text-slate-900">{getChartData().length}</h3>
            <p className="text-sm font-medium text-slate-600">
              {groupBy === "application" 
                ? "Applications" 
                : groupBy === "category" 
                  ? "Categories" 
                  : "Billing Cycles"}
            </p>
          </div>
        </div>
        
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-64 w-full rounded-lg" />
            <Skeleton className="h-32 w-full rounded-lg" />
          </div>
        ) : getChartData().length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
              <div className="w-6 h-6 rounded-full bg-slate-300"></div>
            </div>
            <p className="text-sm font-medium">No data available</p>
            <p className="text-xs text-slate-400 mt-1">Add some licenses to see cost breakdown</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Chart Container - Responsive */}
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Chart */}
              <div className="flex-1 min-h-[300px] lg:min-h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={getChartData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius="80%"
                      innerRadius="40%"
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      strokeWidth={2}
                      stroke="#ffffff"
                    >
                      {getChartData().map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                          className="hover:opacity-80 transition-opacity cursor-pointer"
                        />
                      ))}
                    </Pie>
                    <Tooltip content={customTooltip} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              {/* Custom Legend - Mobile responsive */}
              <div className="w-full lg:w-80">
                <CustomLegend chartData={getChartData()} />
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
